import React, { useCallback, useEffect, useState } from 'react';
import { Check, Eye, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { SigPad } from './SigPad';
import { fmtDate } from '../../utils/timeclock';

const TONE = { 'Awaiting approval': 'bg-amber-100 text-amber-800', Approved: 'bg-green-100 text-green-800', Returned: 'bg-red-100 text-red-800' };

const DecideDialog = ({ file, approve, onClose, onDone }) => {
  const [sig, setSig] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/staff-files/${file.id}/decide`, { approve, signature: sig, note }); toast.success(approve ? 'Approved and countersigned' : 'Returned to the employee'); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="staff-file-decide-dialog">
        <DialogHeader><DialogTitle>{approve ? 'Approve and countersign' : 'Return for correction'}</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">{file.docName} · {file.staffName}{file.periodStart ? ` · ${file.periodStart}` : ''}</p>
        {approve && <SigPad label="Office / supervisor signature" testid="sig-approve" onChange={setSig} />}
        <textarea data-no-dictate value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={approve ? 'Note (optional)' : 'What needs fixing?'} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="staff-file-note" />
        <button disabled={busy || (approve && !sig) || (!approve && note.trim().length < 3)} onClick={go} className="w-full py-2 rounded-lg bg-gray-900 text-[#d4af37] font-semibold disabled:opacity-50" data-testid="staff-file-decide-save">
          {busy ? 'Saving…' : approve ? 'Approve' : 'Return'}
        </button>
      </DialogContent>
    </Dialog>
  );
};

// The employee file: every signed Staff Hub document with its approval status
export const SignedFiles = ({ isOffice, staff }) => {
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState(isOffice ? 'Awaiting approval' : 'All');
  const [who, setWho] = useState('');
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get('/staff-files', { params: { status, ...(who ? { staffId: who } : {}) } }).then((r) => setFiles(r.data.files)).catch(() => toast.error('Could not load files')), [status, who]);
  useEffect(() => { load(); }, [load]);
  const view = async (f) => {
    try {
      const r = await api.get(`/staff-files/${f.id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
    } catch { toast.error('Could not open the file'); }
  };
  return (
    <div className="space-y-3" data-testid="signed-files">
      <div className="flex flex-wrap gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-2 py-1.5 text-sm" data-testid="signed-files-status">
          {['Awaiting approval', 'Approved', 'Returned', 'All'].map((s) => <option key={s}>{s}</option>)}
        </select>
        {isOffice && <select value={who} onChange={(e) => setWho(e.target.value)} className="border rounded px-2 py-1.5 text-sm" data-testid="signed-files-employee">
          <option value="">All employees</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>}
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-[#d4af37] text-left"><tr>{['Document', 'Employee', 'Period', 'Submitted', 'Status', ''].map((h) => <th key={h} className="px-2 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id} className="border-t" data-testid={`signed-file-${f.id}`}>
                <td className="px-2 py-1.5 font-semibold">{f.docName}</td><td className="px-2">{f.staffName}</td><td className="px-2 whitespace-nowrap">{f.periodStart || '—'}</td>
                <td className="px-2 whitespace-nowrap">{fmtDate(f.submittedAt)}</td>
                <td className="px-2"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${TONE[f.status] || ''}`} title={f.note || ''}>{f.status}</span>
                  {f.approvedBy && <span className="block text-xs text-gray-500">by {f.approvedBy}</span>}{f.status === 'Returned' && f.note && <span className="block text-xs text-red-700">{f.note}</span>}</td>
                <td className="px-2 whitespace-nowrap space-x-1">
                  <button onClick={() => view(f)} className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded hover:bg-amber-50" data-testid={`signed-file-view-${f.id}`}><Eye className="w-3 h-3" />View</button>
                  {isOffice && f.status === 'Awaiting approval' && <>
                    <button onClick={() => setDlg({ file: f, approve: true })} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-600 text-white" data-testid={`signed-file-approve-${f.id}`}><Check className="w-3 h-3" />Approve</button>
                    <button onClick={() => setDlg({ file: f, approve: false })} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-red-300 text-red-700" data-testid={`signed-file-return-${f.id}`}><Undo2 className="w-3 h-3" />Return</button>
                  </>}
                </td>
              </tr>
            ))}
            {!files.length && <tr><td colSpan={6} className="px-2 py-4 text-gray-500" data-testid="signed-files-empty">No signed documents here yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {dlg && <DecideDialog {...dlg} onClose={() => setDlg(null)} onDone={load} />}
    </div>
  );
};
